import { useState, useEffect, useRef } from 'react';
import axios from 'axios';

// Country codes with dial codes and expected phone digit lengths
const countryCodes = [
  { code: 'IN', name: 'India', dialCode: '+91', minLength: 10, maxLength: 10 },
  { code: 'US', name: 'United States', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44', minLength: 10, maxLength: 11 },
  { code: 'CA', name: 'Canada', dialCode: '+1', minLength: 10, maxLength: 10 },
  { code: 'AU', name: 'Australia', dialCode: '+61', minLength: 9, maxLength: 9 },
  { code: 'DE', name: 'Germany', dialCode: '+49', minLength: 10, maxLength: 11 },
  { code: 'FR', name: 'France', dialCode: '+33', minLength: 9, maxLength: 9 },
  { code: 'AE', name: 'UAE', dialCode: '+971', minLength: 9, maxLength: 9 },
  { code: 'SG', name: 'Singapore', dialCode: '+65', minLength: 8, maxLength: 8 },
  { code: 'JP', name: 'Japan', dialCode: '+81', minLength: 10, maxLength: 11 },
  { code: 'CN', name: 'China', dialCode: '+86', minLength: 11, maxLength: 11 },
  { code: 'BR', name: 'Brazil', dialCode: '+55', minLength: 10, maxLength: 11 },
  { code: 'ZA', name: 'South Africa', dialCode: '+27', minLength: 9, maxLength: 9 },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64', minLength: 9, maxLength: 10 },
  { code: 'PK', name: 'Pakistan', dialCode: '+92', minLength: 10, maxLength: 10 },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880', minLength: 10, maxLength: 10 },
  { code: 'LK', name: 'Sri Lanka', dialCode: '+94', minLength: 9, maxLength: 9 },
  { code: 'NP', name: 'Nepal', dialCode: '+977', minLength: 10, maxLength: 10 },
  { code: 'MY', name: 'Malaysia', dialCode: '+60', minLength: 9, maxLength: 10 },
  { code: 'PH', name: 'Philippines', dialCode: '+63', minLength: 10, maxLength: 10 },
];

const ContactForm = ({ onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: ''
  });
  const [selectedCountry, setSelectedCountry] = useState(countryCodes[0]); // Default to India
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
    const dropdownRef = useRef(null);

  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
        setCountrySearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = countryCodes.filter(country =>
    country.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
    country.dialCode.includes(countrySearch)
  );

  const validatePhone = (phoneNumber) => {
    const digitsOnly = phoneNumber.replace(/\D/g, '');
    if (digitsOnly.length < selectedCountry.minLength) {
      return `Phone number must be at least ${selectedCountry.minLength} digits`;
    }
    if (digitsOnly.length > selectedCountry.maxLength) {
      return `Phone number must not exceed ${selectedCountry.maxLength} digits`;
    }
    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone is required';
    } else {
      const phoneError = validatePhone(formData.phone);
      if (phoneError) newErrors.phone = phoneError;
    }
    if (!formData.comment.trim()) newErrors.comment = 'Message is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow digits and limit to maxLength
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      const limitedValue = digitsOnly.slice(0, selectedCountry.maxLength);
      setFormData(prev => ({
        ...prev,
        [name]: limitedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setShowCountryDropdown(false);
    setCountrySearch('');
    // Clear phone if it exceeds new country's max length
    if (formData.phone.length > country.maxLength) {
      setFormData(prev => ({
        ...prev,
        phone: prev.phone.slice(0, country.maxLength)
      }));
    }
    if (errors.phone) {
      setErrors(prev => ({ ...prev, phone: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      // Include country code with phone number
      const fullPhone = `${selectedCountry.dialCode} ${formData.phone}`;
      const response = await axios.post(`${apiUrl}/api/contact`, {
        ...formData,
        phone: fullPhone
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', comment: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus(null);
        }, 2000);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleTouchMove = (e) => {
    e.stopPropagation();
  };

  const handleCloseClick = (e) => {
    e.stopPropagation();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4 sm:p-6"
      onClick={handleOverlayClick}
      onTouchMove={handleTouchMove}
    >
      <div 
        className="relative w-full max-w-[420px] max-h-[95vh] overflow-y-auto rounded-2xl border border-slate-100 dark:border-white/5 bg-white dark:bg-dark-800 p-5 sm:p-6 shadow-xl dark:shadow-[0_20px_40px_rgba(0,0,0,0.5)] transition-all duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        
        <button 
          className="absolute top-3 right-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-xl text-gray-500 hover:text-gray-700 dark:bg-dark-700 dark:text-slate-400 dark:hover:bg-dark-600 dark:hover:text-white transition-colors"
          onClick={handleCloseClick}
          aria-label="Close contact form"
        >×</button>
        
        <h2 className="mb-5 text-center text-xl font-bold text-[#4f46e5] dark:text-indigo-400 sm:text-2xl">Get in Touch</h2>

        {submitStatus === 'success' ? (
          <div className="py-8 text-center text-[#10b981]">
            <svg viewBox="0 0 24 24" className="mx-auto mb-4 h-12 w-12 fill-current">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/>
            </svg>
            <h3 className="mb-2 text-2xl font-bold">Thank you!</h3>
            <p className="text-base text-[#6b7280] dark:text-slate-300">Your message has been sent successfully.</p>
          </div>
        ) : submitStatus === 'error' ? (
          <div className="py-8 text-center text-[#ef4444]">
            <svg viewBox="0 0 24 24" className="mx-auto mb-4 h-12 w-12 fill-current">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <h3 className="mb-2 text-2xl font-bold">Oops!</h3>
            <p className="text-base text-[#6b7280] dark:text-slate-300">Something went wrong. Please try again later.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-dark-900 text-gray-800 dark:text-white placeholder-slate-400/70 dark:placeholder-slate-400/80 outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-dark-700'}`}
                placeholder="Name *"
              />
              {errors.name && <span className="mt-1 block text-xs text-red-500">{errors.name}</span>}
            </div>
            
            <div className="relative">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className={`w-full px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-dark-900 text-gray-800 dark:text-white placeholder-slate-400/70 dark:placeholder-slate-400/80 outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] ${errors.email ? 'border-red-500' : 'border-gray-300 dark:border-dark-700'}`}
                placeholder="Email *"
              />
              {errors.email && <span className="mt-1 block text-xs text-red-500">{errors.email}</span>}
            </div>

            {/* Phone Field with Country Code */}
            <div className="relative">
              <label className="block text-xs font-semibold text-[#4f46e5] dark:text-indigo-400 mb-1.5">Mobile Number *</label>
              <div className="flex items-stretch gap-2">
                {/* Country Code Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    className={`flex items-center gap-1 h-full px-2 py-2 rounded-lg border bg-white dark:bg-dark-900 text-sm hover:border-[#4f46e5] focus:border-[#4f46e5] focus:outline-none ${errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-dark-700'}`}
                  >
                    <span className="text-gray-800 dark:text-slate-200 font-medium">{selectedCountry.code}</span>
                    <span className="text-gray-600 dark:text-slate-400">{selectedCountry.dialCode}</span>
                    <svg className={`w-3 h-3 text-gray-500 ${showCountryDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showCountryDropdown && (
                    <div className="absolute top-full left-0 mt-1 w-56 max-h-48 overflow-y-auto bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-700 rounded-lg shadow-lg z-50">
                      {/* Search Input */}
                      <div className="sticky top-0 bg-white dark:bg-dark-800 p-2 border-b border-gray-200 dark:border-dark-700">
                        <input
                          type="text"
                          placeholder="Search country..."
                          value={countrySearch}
                          onChange={(e) => setCountrySearch(e.target.value)}
                          className="w-full px-2 py-1.5 text-sm border border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 text-gray-800 dark:text-white placeholder-slate-400/70 dark:placeholder-slate-400/80 rounded focus:border-[#4f46e5] focus:outline-none"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      {/* Country List */}
                      {filteredCountries.map((country) => (
                        <button
                          key={country.code}
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-dark-700 ${selectedCountry.code === country.code ? 'bg-indigo-50 dark:bg-indigo-950/40' : ''}`}
                        >
                          <span className="text-gray-800 dark:text-slate-200 font-medium w-8">{country.code}</span>
                          <span className="text-gray-700 dark:text-slate-300 flex-1">{country.name}</span>
                          <span className="text-gray-500 dark:text-slate-400">{country.dialCode}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Phone Input */}
                <div className="flex-1 relative">
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    maxLength={selectedCountry.maxLength}
                    className={`w-full h-full px-3 py-2.5 rounded-lg border bg-white dark:bg-dark-900 text-sm text-gray-800 dark:text-white placeholder-slate-400/70 dark:placeholder-slate-400/80 outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] ${errors.phone ? 'border-red-500' : 'border-gray-300 dark:border-dark-700'}`}
                    placeholder={`Enter ${selectedCountry.minLength}-${selectedCountry.maxLength} digits`}
                  />
                  {/* Digit Counter */}
                  <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${formData.phone.length >= selectedCountry.minLength ? 'text-green-500' : 'text-gray-400 dark:text-slate-400'}`}>
                    {formData.phone.length}/{selectedCountry.maxLength}
                  </span>
                </div>
              </div>
              {errors.phone && <span className="mt-1 block text-xs text-red-500">{errors.phone}</span>}
            </div>
            
            <div className="relative">
              <textarea
                id="comment"
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                required
                className={`w-full resize-none px-3 py-2.5 text-sm border rounded-lg bg-white dark:bg-dark-900 text-gray-800 dark:text-white placeholder-slate-400/70 dark:placeholder-slate-400/80 outline-none focus:border-[#4f46e5] focus:ring-1 focus:ring-[#4f46e5] ${errors.comment ? 'border-red-500' : 'border-gray-300 dark:border-dark-700'}`}
                rows="3"
                placeholder="Message *"
              />
              {errors.comment && <span className="mt-1 block text-xs text-red-500">{errors.comment}</span>}
            </div>
            
            <button 
              type="submit" 
              className="mt-2 w-full rounded-lg bg-[#4f46e5] dark:bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#4338ca] dark:hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-dark-700 dark:disabled:text-slate-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContactForm;